import React, { useState, useEffect } from 'react';

import { useAlert } from '@weave/alert-system';
import { theme } from '@weave/theme';
import { Text } from '@weave/design-system';

import {
  reportEntityTagState,
  useCreateEntityTagMutation,
  useDeleteEntityTagMutation,
} from 'apis/wetags';
import { OnOffIndicator } from 'components/shared/on-off-indicator/on-off-indicator.component';
import { ToggleSwitch } from 'components/shared/toggle-switch/toggle-switch.component';
import { InfoTooltip } from 'components/shared/info-tooltip/info-tooltip.component';
import { EntityTagEvents } from './wetags-entity-events.component';
import {
  EntityTagState,
  ReportEntityTagStateResponse,
  Tag,
  TagExistReason,
} from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';

type WetagsEntityProps = {
  tag: Tag | undefined;
  tagState: EntityTagState;
  id: string;
  et: EntityTagState;
  globalEdit?: boolean;
};

const getPercentageOnExplanation = (percentage?: number) => {
  return (
    `Even though it is not explicitly applied, this tag is set due to tag rollout percentage of ${percentage}%\n\n` +
    'Removing a tag here will not actually change the tag state. Tags matching a percentage can never be removed without lowering the percentage of locations they are applied to.'
  );
};

export const WetagsEntity = ({ tag, tagState, id, globalEdit }: WetagsEntityProps) => {
  console.log(tagState);

  const alert = useAlert();

  const [editable, setEditable] = useState(false);
  const [selected, setSelected] = useState(false);
  const [state, setState] = useState<EntityTagState>(tagState);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefetchTag = () => {
    reportEntityTagState({
      namespaceId: tagState?.namespaceId ?? '',
      tagId: tagState?.tagId,
      entityId: id,
    })
      .then((response) => {
        if (!response.state) {
          throw new Error('No tag state returned');
        }
        setState(response.state);
      })
      .catch((error) => {
        alert.error({
          message: 'Failed to check tag status: ' + error,
        });
      });
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const { mutate: addEntityTag } = useCreateEntityTagMutation({
    onSuccess: () => {
      alert.success({
        message: 'Successfully added tag.',
      });
      handleRefetchTag();
    },
    onError: (error) => {
      alert.error({
        message: 'Failed to add tag:' + error,
      });
    },
  });

  const { mutate: removeEntityTag } = useDeleteEntityTagMutation({
    onSuccess: () => {
      alert.success({
        message: 'Successfully removed tag.',
      });
      handleRefetchTag();
    },
    onError: (error) => {
      alert.error({
        message: 'Failed to remove tag:' + error,
      });
    },
  });

  return (
    <>
      <div
        css={{
          padding: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div>
          <span
            css={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setEditable(!editable);
            }}
            title="Click to edit"
          >
            <OnOffIndicator css={{ cursor: 'pointer' }} on={state.exists ?? false} />
          </span>
          {(globalEdit || editable) && (
            <div css={{ position: 'relative' }}>
              <ToggleSwitch
                label=""
                on={state.reason == TagExistReason.ENTITY_EXPLICITLY_TAGGED}
                onChange={async () => {
                  if (state.reason == TagExistReason.ENTITY_EXPLICITLY_TAGGED) {
                    removeEntityTag({
                      namespaceId: tagState?.namespaceId ?? '',
                      tagId: tagState?.tagId,
                      entityId: id,
                    });
                  } else {
                    addEntityTag({
                      entityTag: {
                        namespaceId: tagState?.namespaceId ?? '',
                        tagId: tagState?.tagId,
                        entityId: id,
                      },
                    });
                  }
                }}
              />
            </div>
          )}
        </div>
        <span
          css={{
            cursor: 'pointer',
          }}
          onClick={() => {
            setSelected(!selected);
          }}
        >
          <Text size="large" css={{ margin: `0 0 0 ${theme.spacing(2)}` }}>
            <div css={{ float: 'left' }} title="Click to view events">
              {tagState?.tagId}: {tag?.description} ({tagState?.reason})
            </div>
            {state.exists && state.reason == TagExistReason.ENTITY_IN_TAG_PERCENT && (
              <div css={{ float: 'right' }}>
                <InfoTooltip
                  css={{ marginLeft: theme.spacing(1) }}
                  body={getPercentageOnExplanation(tag?.percentage)}
                  tooltipPlacement="auto"
                />
              </div>
            )}
          </Text>
        </span>
      </div>
      {selected && (
        <EntityTagEvents
          namespaceId={tagState?.namespaceId ?? ''}
          tagId={tagState?.tagId ?? ''}
          entityId={id}
          refreshKey={refreshKey}
        />
      )}
    </>
  );
};
